import {useCallback, useRef, useState} from 'react';
import {useLatest} from 'ahooks';
import type {DesignAttachment, PromptAttachment} from '@Components/ai-prompt-box/components/ai-prompt-box-attachments/ai-prompt-box-attachments.types';
import {getAttachmentPreviewUrl, getDesignAttachmentScreenURLKey, isDesignAttachment} from '@Components/ai-prompt-box/libraries/ai-prompt-box-attachment.library';
import {getPosterScreenPreviewURL} from '@Libraries/poster-library';

export interface UseDesignAttachmentScreenURLsReturn {
  designScreenURLs: Record<string, string>;
  prefetchDesignScreenURLAtIndex: (index: number) => void;
}

export function useDesignAttachmentScreenURLs(attachments: PromptAttachment[]): UseDesignAttachmentScreenURLsReturn {
  const [designScreenURLs, setDesignScreenURLs] = useState<Record<string, string>>({});
  const fetchStartedKeysRef = useRef<Set<string>>(new Set());
  const attachmentsRef = useLatest(attachments);

  const fetchDesignScreenURL = useCallback(async (attachment: DesignAttachment): Promise<void> => {
    const cacheKey = getDesignAttachmentScreenURLKey(attachment.designId, attachment.pageHashedId);
    if (fetchStartedKeysRef.current.has(cacheKey)) {
      return;
    }
    fetchStartedKeysRef.current.add(cacheKey);

    const screenURL = await getPosterScreenPreviewURL(attachment.designId, attachment.pageHashedId).catch(() => null);
    const resolvedURL = screenURL ?? getAttachmentPreviewUrl(attachment, true);
    setDesignScreenURLs((current) => (cacheKey in current ? current : {...current, [cacheKey]: resolvedURL}));
  }, []);

  const prefetchDesignScreenURLAtIndex = useCallback((index: number): void => {
    const attachment = attachmentsRef.current[index];
    if (attachment && isDesignAttachment(attachment)) {
      void fetchDesignScreenURL(attachment);
    }
  }, [attachmentsRef, fetchDesignScreenURL]);

  return {designScreenURLs, prefetchDesignScreenURLAtIndex};
}
